import { byUrgency, NEED_SENTENCE, ofPhrase, type Need } from "../home";

// What needs a person, across every class — above whatever lesson is open.
//
// It is a strip rather than a screen. Reviewing and adjusting student work is the job; this is the
// short list of things that cannot be done in order, and a teacher should meet it on the way to
// the work rather than at a destination they have to remember to visit.
//
// The reason leads every row. The old queue led with a name and put the reason in the pane beside
// it, so the list could not be scanned — you had to open each paper to find out why it was there.
// What you are choosing between is reasons.
//
// Every row names its class, because the strip spans them: "Maya R. · Counterclaims" is only
// unambiguous while a teacher has one class, which is the case that never lasts.

const SHOWN = 4;

export function NeedsStrip({ needs, onOpen }: {
  needs: Need[];
  onOpen: (href: string) => void;
}) {
  const ordered = byUrgency(needs);
  // Nothing waiting says so rather than disappearing: a strip that vanishes when it is empty is
  // how someone stops noticing that nothing is coming back.
  // NOTHING, when there is nothing. The strip used to say "nothing is waiting on you" over an
  // empty row, which is a band of chrome reporting the absence of news — and it sat above the
  // work whether or not it had anything to say. Where work IS waiting, the folder holding it now
  // carries a count in the tree, which is where a teacher goes looking for it.
  if (ordered.length === 0) return null;
  const shown = ordered.slice(0, SHOWN);
  return (
    <div className="ns">
      <span className="ns-label">Needs your attention</span>
      <ul className="ns-list">
        {shown.map((n, i) => (
          <li key={`${n.kind}-${n.subject}-${i}`} className={`ns-item ns-${n.kind}`}>
            <button onClick={() => onOpen(n.href)}>
              <span className="ns-why">{NEED_SENTENCE[n.kind]}</span>
              <span className="ns-who">{n.subject}</span>
              <span className="ns-where">{n.class_label}</span>
              {n.of && <span className="ns-of">{ofPhrase(n.of)}</span>}
            </button>
          </li>
        ))}
      </ul>
      {/* The count of what is not shown, not a "more" that hides how much more. */}
      {ordered.length > shown.length && (
        <span className="ns-rest">and {ordered.length - shown.length} more</span>
      )}
    </div>
  );
}
